import React, { useState, useEffect, Fragment } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { collection, getDocs, doc, updateDoc, getDoc } from 'firebase/firestore';
import { db } from '../../firebase';
import { DragDropContext, Draggable } from 'react-beautiful-dnd';
import DeliveryList from './DeliveryList';
import { downloadPDF } from '../../utils/pdfUtils';
import { StrictModeDroppable } from './StrictModeDroppable';

export default function SelectVehicleModal({ isOpen, onClose, orders, customerDetails, businessDetails }) {
  const [vehicles, setVehicles] = useState([]);
  const [selectedVehicle, setSelectedVehicle] = useState('');
  const [orderedCustomers, setOrderedCustomers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchVehicles = async () => {
      try {
        const vehiclesRef = collection(db, 'vehicles');
        const snapshot = await getDocs(vehiclesRef);
        const vehiclesList = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        setVehicles(vehiclesList);
      } catch (error) {
        console.error('Error fetching vehicles:', error);
      }
    };
    fetchVehicles();
  }, []);

  useEffect(() => {
    const fetchCustomerOrder = async () => {
      setIsLoading(true);
      if (selectedVehicle) {
        try {
          const vehicleOrders = orders.filter(order => order.vehicleId === selectedVehicle);
          
          const vehicleDoc = await getDoc(doc(db, 'vehicles', selectedVehicle));
          const data = vehicleDoc.data();
          
          if (data?.customerOrder) {
            const savedOrder = data.customerOrder;
            const currentCustomers = vehicleOrders.map(order => order.customerId);
            
            const orderedList = savedOrder
              .filter(id => currentCustomers.includes(id))
              .map(customerId => vehicleOrders.find(order => order.customerId === customerId));
            
            const remainingOrders = vehicleOrders.filter(
              order => !savedOrder.includes(order.customerId)
            );
            
            setOrderedCustomers([...orderedList, ...remainingOrders]);
          } else {
            setOrderedCustomers(vehicleOrders);
          }
        } catch (error) {
          console.error('Error fetching customer order:', error);
          setOrderedCustomers([]);
        }
      } else {
        setOrderedCustomers([]);
      }
      setIsLoading(false);
    };
    fetchCustomerOrder();
  }, [selectedVehicle, orders]);

  const handleDragEnd = async (result) => {
    if (!result.destination) return;

    const items = Array.from(orderedCustomers);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);

    setOrderedCustomers(items);

    if (selectedVehicle) {
      try {
        const vehicleRef = doc(db, 'vehicles', selectedVehicle);
        await updateDoc(vehicleRef, {
          customerOrder: items.map(order => order.customerId)
        });
      } catch (error) {
        console.error('Error saving customer order:', error);
      }
    }
  };

  const vehicle = vehicles.find(v => v.id === selectedVehicle);

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/25 dark:bg-black/40" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-2xl bg-white dark:bg-gray-800 p-6 text-left align-middle shadow-xl transition-all">
              <div className="flex items-center justify-between mb-4">
                <Dialog.Title className="text-lg font-medium text-gray-900 dark:text-white">
                  Select Delivery Vehicle
                </Dialog.Title>
                <button onClick={onClose} className="text-gray-400 hover:text-gray-500">
                  <XMarkIcon className="h-6 w-6" />
                </button>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Select Vehicle
                  </label>
                  <select
                    value={selectedVehicle}
                    onChange={(e) => setSelectedVehicle(e.target.value)}
                    className="w-full px-3 py-2 rounded-md border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                  >
                    <option value="">Select a vehicle</option>
                    {vehicles.map(vehicle => (
                      <option key={vehicle.id} value={vehicle.id}>
                        {vehicle.registrationNumber} - {vehicle.driverName}
                      </option>
                    ))}
                  </select>
                </div>

                {selectedVehicle && !isLoading && orderedCustomers.length > 0 && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Arrange Delivery Order
                    </label>
                    <DragDropContext onDragEnd={handleDragEnd}>
                      <StrictModeDroppable droppableId="customers">
                        {(provided) => (
                          <div
                            {...provided.droppableProps}
                            ref={provided.innerRef}
                            className="space-y-2"
                          >
                            {orderedCustomers.map((order, index) => (
                              <Draggable
                                key={order.id}
                                draggableId={order.id}
                                index={index}
                              >
                                {(provided, snapshot) => (
                                  <div
                                    ref={provided.innerRef}
                                    {...provided.draggableProps}
                                    {...provided.dragHandleProps}
                                    className={`p-3 rounded-lg border transition-colors duration-200
                                      ${snapshot.isDragging
                                        ? 'bg-primary-50 border-primary-300 shadow-lg dark:bg-primary-900/50 dark:border-primary-700'
                                        : 'bg-gray-50 border-gray-200 dark:bg-gray-700 dark:border-gray-600'
                                      }
                                      hover:bg-gray-100 dark:hover:bg-gray-600 cursor-move`}
                                  >
                                    <div className="flex items-center justify-between">
                                      <div className="flex-1">
                                        <div className="font-medium text-gray-900 dark:text-white">
                                          {order.customerName}
                                        </div>
                                        <div className="text-sm text-gray-500 dark:text-gray-400">
                                          {order.items.map(item => 
                                            `${item.vegetable} (${item.estimatedQuantity}kg)`
                                          ).join(', ')}
                                        </div>
                                      </div>
                                      <div className="ml-4 flex-shrink-0 text-gray-400">
                                        <svg 
                                          className="h-5 w-5" 
                                          fill="none" 
                                          viewBox="0 0 24 24" 
                                          stroke="currentColor"
                                        >
                                          <path 
                                            strokeLinecap="round" 
                                            strokeLinejoin="round" 
                                            strokeWidth={2} 
                                            d="M4 8h16M4 16h16" 
                                          />
                                        </svg>
                                      </div>
                                    </div>
                                  </div>
                                )}
                              </Draggable>
                            ))}
                            {provided.placeholder}
                          </div>
                        )}
                      </StrictModeDroppable>
                    </DragDropContext>
                  </div>
                )}

                {selectedVehicle && !isLoading && orderedCustomers.length === 0 && (
                  <div className="text-center py-4 text-gray-500 dark:text-gray-400">
                    No customers assigned to this vehicle
                  </div>
                )}

                <div className="mt-6 flex flex-col-reverse sm:flex-row gap-2 sm:justify-end">
                  <button
                    type="button"
                    onClick={onClose}
                    className="w-full sm:w-auto px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 
                      bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg
                      hover:bg-gray-50 dark:hover:bg-gray-600"
                  >
                    Cancel
                  </button>
                  {selectedVehicle && orderedCustomers.length > 0 && (
                    <button
                      onClick={async () => {
                        const doc = (
                          <DeliveryList 
                            orders={orderedCustomers}
                            date={new Date()}
                            customerDetails={customerDetails}
                            vehicle={vehicle}
                            businessDetails={businessDetails}
                          />
                        );
                        await downloadPDF(doc, `delivery-list-${vehicle.registrationNumber}-${new Date().toISOString().split('T')[0]}.pdf`);
                      }}
                      className="w-full sm:w-auto px-4 py-2 text-sm font-medium text-white 
                        bg-primary-600 rounded-lg border border-transparent
                        hover:bg-primary-700 
                        focus:outline-none focus:ring-2 focus:ring-primary-500"
                    >
                      Download PDF
                    </button>
                  )}
                </div>
              </div>
            </Dialog.Panel>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}